import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import LogLayout from "../components/LogLayout";
import SectionTabs, { TOURS_TABS } from "../components/SectionTabs";
import { Truck, Plus, X, MapPin, Phone, User, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const DEL_STATUSES = [
  { value: "pending", label: "En attente", color: "bg-white/50/20 text-slate-400 border-slate-500/30" },
  { value: "picked_up", label: "Récupéré", color: "bg-blue-500/100/20 text-blue-400 border-blue-500/30" },
  { value: "in_transit", label: "En transit", color: "bg-cyan-500/100/20 text-cyan-400 border-cyan-500/30" },
  { value: "delivered", label: "Livré", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  { value: "signed", label: "Décharge signée", color: "bg-green-500/100/20 text-green-400 border-green-500/30" },
  { value: "failed", label: "Échec", color: "bg-red-500/100/20 text-red-400 border-red-500/30" },
];

const getStatusStyle = (s) => DEL_STATUSES.find(st => st.value === s)?.color || "";
const getStatusLabel = (s) => DEL_STATUSES.find(st => st.value === s)?.label || s;

export default function LogDeliveriesPage() {
  const [deliveries, setDeliveries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [form, setForm] = useState({ invoice_ids: [], destination_name: "", destination_address: "", courier_name: "", courier_phone: "", document_type: "facture", note: "", insurer_filter: "" });
  const [insurers, setInsurers] = useState([]);

  const load = useCallback(async () => {
    try {
      const h = getHeaders();
      const [delRes, invRes, statsRes, insRes] = await Promise.all([
        axios.get(`${API}/log/deliveries`, { headers: h, params: filterStatus ? { status: filterStatus } : {} }),
        axios.get(`${API}/log/invoices`, { headers: h }),
        axios.get(`${API}/log/deliveries/stats`, { headers: h }),
        axios.get(`${API}/log/insurers`, { headers: h }),
      ]);
      setDeliveries(delRes.data.deliveries || []);
      setInvoices(invRes.data.invoices || []);
      setStats(statsRes.data);
      setInsurers(insRes.data.insurers || []);
    } catch { }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.invoice_ids.length === 0) { toast.error("Selectionnez au moins une facture"); return; }
    try {
      await axios.post(`${API}/log/deliveries`, form, { headers: getHeaders() });
      toast.success("Expédition créée");
      setShowCreate(false);
      setForm({ invoice_ids: [], destination_name: "", destination_address: "", courier_name: "", courier_phone: "", document_type: "facture", note: "", insurer_filter: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleStatusUpdate = async (delId, newStatus, signedBy) => {
    try {
      const body = { status: newStatus };
      if (signedBy) body.signed_by = signedBy;
      await axios.put(`${API}/log/deliveries/${delId}/status`, body, { headers: getHeaders() });
      toast.success("Statut mis a jour");
      setShowStatusUpdate(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleInvoice = (id) => {
    setForm(f => ({
      ...f,
      invoice_ids: f.invoice_ids.includes(id) ? f.invoice_ids.filter(i => i !== id) : [...f.invoice_ids, id]
    }));
  };

  return (
    <LogLayout title="Tournées" subtitle="Expéditions : acheminement des documents et décharges">
      <SectionTabs tabs={TOURS_TABS} />
      <div data-testid="log-deliveries-page" className="space-y-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
            {DEL_STATUSES.filter(s => (stats.by_status?.[s.value] || 0) > 0).map(s => (
              <div key={s.value} className="bg-slate-900 border border-white/5 rounded-xl p-3 text-center">
                <p className={`text-lg font-bold ${s.color.split(" ")[1]}`}>{stats.by_status[s.value]}</p>
                <p className="text-[10px] text-slate-500 uppercase">{s.label}</p>
              </div>
            ))}
            {stats.total > 0 && (
              <div className="bg-slate-900 border border-white/5 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-emerald-400">{stats.success_rate}%</p>
                <p className="text-[10px] text-slate-500 uppercase">Taux succès</p>
              </div>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <select data-testid="delivery-filter-status" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setLoading(true); }} className="bg-slate-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none">
            <option value="">Tous les statuts</option>
            {DEL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <Button data-testid="create-delivery-btn" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-2">
            <Plus className="w-4 h-4" /> Nouvelle expédition
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
        ) : deliveries.length === 0 ? (
          <div className="text-center py-16 bg-slate-900 rounded-xl border border-white/5">
            <Truck className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucune expédition</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deliveries.map(del => (
              <div key={del.id} data-testid={`delivery-row-${del.id}`} className="bg-slate-900 border border-white/5 rounded-xl p-4 hover:border-blue-500/20 transition-colors cursor-pointer" onClick={() => setShowDetail(del)}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/100/15 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{del.destination_name}</p>
                      <p className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{del.destination_address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-slate-400">{del.invoices_summary?.length || 0} facture(s)</p>
                      {del.courier_name && <p className="text-[10px] text-slate-500 flex items-center gap-1 justify-end"><User className="w-3 h-3" />{del.courier_name}</p>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setShowStatusUpdate(del); }} className={`px-2.5 py-1 rounded-full border text-[11px] font-medium cursor-pointer hover:opacity-80 ${getStatusStyle(del.status)}`} data-testid={`delivery-status-${del.id}`}>
                      {getStatusLabel(del.status)}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="create-delivery-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h2 className="text-lg font-bold text-white">Nouvelle expédition</h2>
                <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-5 space-y-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-2">Factures à livrer *</label>
                  {/* Insurer filter */}
                  <div className="mb-2">
                    <label className="text-[10px] text-slate-500 block mb-1">Filtrer par assureur</label>
                    <select
                      value={form.insurer_filter}
                      onChange={e => setForm(f => ({ ...f, insurer_filter: e.target.value, invoice_ids: [] }))}
                      className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                      data-testid="insurer-filter-select"
                    >
                      <option value="">Tous les assureurs</option>
                      {insurers.map(ins => (
                        <option key={ins.id} value={ins.name}>{ins.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1 bg-slate-950 border border-white/10 rounded-lg p-2">
                    {invoices.filter(inv => !form.insurer_filter || inv.insurer_name === form.insurer_filter).length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-2">Aucune facture disponible{form.insurer_filter ? ` pour ${form.insurer_filter}` : ""}</p>
                    ) : invoices.filter(inv => !form.insurer_filter || inv.insurer_name === form.insurer_filter).map(inv => (
                      <label key={inv.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-sm">
                        <input type="checkbox" checked={form.invoice_ids.includes(inv.id)} onChange={() => toggleInvoice(inv.id)} className="rounded border-white/20 bg-slate-900" data-testid={`select-invoice-${inv.id}`} />
                        <span className="text-white">{inv.invoice_number}</span>
                        <span className="text-slate-500 text-xs ml-auto">{inv.insurer_name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">{form.invoice_ids.length} facture(s) sélectionnée(s)</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Destination *</label>
                    <input data-testid="delivery-dest-name" value={form.destination_name} onChange={e => setForm(f => ({ ...f, destination_name: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" placeholder="Nom assureur / agence" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Type document</label>
                    <select data-testid="delivery-doc-type" value={form.document_type} onChange={e => setForm(f => ({ ...f, document_type: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none">
                      <option value="facture">Factures</option>
                      <option value="bordereau">Bordereau</option>
                      <option value="decharge">Decharge</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Adresse destination *</label>
                  <input data-testid="delivery-dest-address" value={form.destination_address} onChange={e => setForm(f => ({ ...f, destination_address: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Agent Commercial</label>
                    <input data-testid="delivery-courier-name" value={form.courier_name} onChange={e => setForm(f => ({ ...f, courier_name: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Tel agent commercial</label>
                    <input data-testid="delivery-courier-phone" value={form.courier_phone} onChange={e => setForm(f => ({ ...f, courier_phone: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Note</label>
                  <textarea data-testid="delivery-note" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-white/10 text-slate-300">Annuler</Button>
                  <Button type="submit" data-testid="submit-delivery-btn" className="flex-1 bg-blue-600 hover:bg-blue-500/100 text-white">Créer</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {showStatusUpdate && (
          <DeliveryStatusModal del={showStatusUpdate} onClose={() => setShowStatusUpdate(null)} onUpdate={handleStatusUpdate} />
        )}

        {/* Détail Modal */}
        {showDetail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="delivery-detail-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h2 className="text-lg font-bold text-white">{showDetail.destination_name}</h2>
                <button onClick={() => setShowDetail(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-[10px] text-slate-500 uppercase">Adresse</p><p className="text-sm text-white">{showDetail.destination_address}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Agent Commercial</p><p className="text-sm text-white">{showDetail.courier_name || "-"}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Documents</p><p className="text-sm text-white">{showDetail.invoices_summary?.length || 0} facture(s)</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Statut</p><span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${getStatusStyle(showDetail.status)}`}>{getStatusLabel(showDetail.status)}</span></div>
                  {showDetail.signed_by && <div><p className="text-[10px] text-slate-500 uppercase">Signe par</p><p className="text-sm text-emerald-400">{showDetail.signed_by}</p></div>}
                </div>
                {showDetail.invoices_summary?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 font-semibold mb-2 uppercase">Factures incluses</p>
                    <div className="space-y-1">
                      {showDetail.invoices_summary.map((inv, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-slate-950 rounded-lg">
                          <FileText className="w-3.5 h-3.5 text-blue-400" />
                          <span className="text-xs text-white">{inv.number}</span>
                          <span className="text-[10px] text-slate-500 ml-auto">{inv.insurer}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {showDetail.tracking?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 font-semibold mb-2 uppercase">Suivi</p>
                    <div className="space-y-2">
                      {showDetail.tracking.map((t, i) => (
                        <div key={i} className="flex items-start gap-3 p-2 bg-slate-950 rounded-lg">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${getStatusStyle(t.status).split(" ")[0]}`} />
                          <div>
                            <p className="text-xs text-white">{getStatusLabel(t.status)} <span className="text-slate-500 ml-1">{t.note}</span></p>
                            <p className="text-[10px] text-slate-500">{new Date(t.date).toLocaleString("fr-FR")} - {t.by}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}

function DeliveryStatusModal({ del, onClose, onUpdate }) {
  const [signedBy, setSignedBy] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="delivery-status-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h2 className="text-base font-bold text-white">Modifier le statut</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-2">
          <p className="text-xs text-slate-400 mb-3">Destination: <span className="text-white">{del.destination_name}</span></p>
          {DEL_STATUSES.map(s => (
            <button key={s.value} onClick={() => {
              if (s.value === "signed" && !signedBy) { toast.error("Saisissez le nom du signataire"); return; }
              onUpdate(del.id, s.value, s.value === "signed" ? signedBy : undefined);
            }} data-testid={`set-del-status-${s.value}`}
              className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all ${del.status === s.value ? s.color + " font-semibold" : "border-white/5 text-slate-400 hover:bg-white/5"}`}>
              {s.label} {del.status === s.value && "(actuel)"}
            </button>
          ))}
          <div className="pt-3 border-t border-white/5 mt-3">
            <label className="text-xs text-slate-400 block mb-1">Signe par (pour decharge)</label>
            <input data-testid="delivery-signed-by" value={signedBy} onChange={e => setSignedBy(e.target.value)} placeholder="Nom du signataire" className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
          </div>
        </div>
      </div>
    </div>
  );
}
